import axios from "axios";
import { queueRequest } from "./offlineQueue";
import { resolveBackendUrl } from "./resolveBackendUrl";

// Single source of truth for the backend base URL. See `resolveBackendUrl`
// for the host-mismatch auto-correction logic that fixed the 2026-05-12
// production login loop.
export const BACKEND_URL = resolveBackendUrl(
  process.env.REACT_APP_BACKEND_URL,
  typeof window !== "undefined" ? window.location : null,
);
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  // We use Bearer-token auth (Authorization header set in the request
  // interceptor below). The cookie is a redundant fallback the backend
  // sets — but `withCredentials: true` triggers a strict CORS preflight
  // that iOS Safari in PWA standalone mode silently hangs on, leaving
  // /auth/me & /auth/login Promises pending forever (the "Loading…"
  // bug on iPhone home-screen installs). Turning off credentials makes
  // the token path the only path, which works everywhere. Locked 2026-05.
  withCredentials: false,
  // Default timeout — Wave 25.12 raised from 12s to 60s. The old 12s
  // value was murdering legitimate AI calls (Whisper transcribe + Claude
  // refine routinely takes 15–25s; TTS 3–10s; large uploads 15–30s). Each
  // killed call would then be wrongly queued as "offline" and eventually
  // duplicate the work on retry. 60s is the new ceiling for any single
  // request — endpoints that need more (intake = 90s) override locally.
  timeout: 60_000,
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("hv_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// ─────────────────────────────────────────────────────────────────────
// Wave 25.12 — Offline-aware mutation interceptor REWORKED.
// ─────────────────────────────────────────────────────────────────────
// PRIOR BUG: any error without a `response` was treated as offline →
// queued for replay. But an axios timeout (`ECONNABORTED`) ALSO has no
// response, so a slow AI endpoint that took >12s was queued even though
// the user was online and the backend may have completed the work. This
// produced (a) silent failures, (b) duplicate AI calls on retry, and
// (c) an unstoppable retry loop when the JWT expired and the saved
// headers kept returning 401/5xx.
//
// New rules:
//   1. Only queue when the device is ACTUALLY OFFLINE
//      (`navigator.onLine === false`). A pure timeout on an online
//      device is treated as a real error and bubbles to the caller.
//   2. NEVER queue non-idempotent / AI endpoints. Retrying a TTS or
//      transcribe call costs money, produces a fresh result, and may
//      be a duplicate of work the backend already completed. The user
//      gets a real error toast and can re-trigger explicitly.
//   3. GETs are never queued (already the case).
// ─────────────────────────────────────────────────────────────────────

const MUTATING = new Set(["post", "put", "patch", "delete"]);

// URL substring patterns that must NEVER be queued offline. Each of
// these is non-idempotent (charges $/produces a fresh artefact) and a
// stale replay 4 hours later is worse than failing loudly in the
// moment. User explicitly chose option 1a — fail loudly.
const NON_QUEUEABLE_PATTERNS = [
  "/voice/transcribe",
  "/voice/refine",
  "/voice/transcribe-refine",
  "/hidro/voice/",          // /hidro/voice/speak + /hidro/voice/transcribe
  "/hidro/chat/",           // /chat/start + /chat/result/{id}
  "/hidro/intake",
  "/hidro/intakes/",
  "/engineering/sessions/", // engineering desk Claude calls
  "/research/synthesize",
  "/coach/checkin",         // each call hits AI extractor
  "/uploads",               // file uploads — usually big, retrying duplicates
  "/bulk-import/",          // OCR extraction is expensive AI
  "/inventory/import/",
  "/suppliers/import/",
  "/datasheets/extract",
  "/push/subscribe",        // expires fast; stale replay is pointless
];

function isNonQueueable(url) {
  if (!url) return false;
  return NON_QUEUEABLE_PATTERNS.some((p) => url.includes(p));
}

api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const cfg = error.config || {};
    const method = (cfg.method || "get").toLowerCase();
    const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
    // Distinguish TIMEOUT (server may have completed; do NOT queue) from
    // a real network drop / DNS failure / TLS error.
    const isTimeout = error.code === "ECONNABORTED"
      || error.code === "ETIMEDOUT"
      || /timeout/i.test(error.message || "");
    const hasNoResponse = !error.response;
    const isRealNetworkError = hasNoResponse && !isTimeout;

    const url = (cfg.baseURL || "") + (cfg.url || "");
    const queueable = MUTATING.has(method) && !isNonQueueable(url);

    // ── 2026-02 — Stanley's "Token expired" fix ────────────────────────
    // Any 401 from the backend (Token expired / Invalid token / Not
    // authenticated) means the user's session is dead. We:
    //   1. Clear the local token + cached user.
    //   2. Stash a flag so the login screen can show "Your session expired".
    //   3. Hard-redirect to /login so every in-flight component (which
    //      would otherwise show 50 toasts back-to-back) is unmounted.
    //
    // We skip the redirect when:
    //   - We're already on /login (avoids a redirect loop).
    //   - The failing call is /auth/me or /auth/login (AuthContext already
    //     handles these and an unsolicited redirect would interrupt the
    //     normal "show the login form" path on first paint).
    //   - The failing call is /auth/session-preference itself (caller
    //     handles re-auth manually).
    if (
      error.response && error.response.status === 401
      && typeof window !== "undefined"
      && !/\/auth\/(me|login|claim-password|session-preference)/.test(cfg.url || "")
      && !window.location.pathname.startsWith("/login")
    ) {
      // ── [Iter 328 — 2026-02-24] Impersonation-safe 401 handling ──
      // When staff impersonate a customer we swap `hv_token` for a
      // short-lived CLIENT token and save the staff's real token in
      // `hv_impersonator_token`. Long-running portal actions (e.g.
      // Download PDF that embeds 30 photos and renders for 60s) can
      // outlive the client token. When that happens, the old handler
      // wiped `hv_token` and redirected to /login — the STAFF user
      // saw themselves signed out completely even though their own
      // session was still perfectly valid in `hv_impersonator_token`.
      //
      // Now: on 401 during impersonation we AUTO-RESTORE the staff
      // token, drop the impersonation banner, and reject the promise
      // so the caller shows its own toast. The user stays signed in.
      let imp_token = null;
      try {
        imp_token = localStorage.getItem("hv_impersonator_token");
      } catch (_) { /* ignore */ }
      if (imp_token) {
        try {
          localStorage.setItem("hv_token", imp_token);
          localStorage.removeItem("hv_impersonator_token");
          localStorage.removeItem("hv_impersonator_user");
          // Tell any mounted impersonation banner / AuthContext to
          // re-read `hv_token`. AuthContext listens for storage +
          // this custom event.
          window.dispatchEvent(new CustomEvent("hv:impersonation-changed"));
        } catch (_) { /* ignore */ }
        // Do NOT redirect. Let the caller's own toast / catch handle
        // the failure. The next render will pick up the staff session.
        return Promise.reject(error);
      }
      try {
        sessionStorage.setItem("hv_session_expired", "1");
        localStorage.removeItem("hv_token");
        localStorage.removeItem("hv:user");
      } catch (_) { /* private mode / quota — ignore */ }
      // Use replace so the back button doesn't loop back to the dead page.
      try {
        window.location.replace("/login?expired=1");
      } catch (_) { /* ignore */ }
      return Promise.reject(error);
    }

    // Queue ONLY when:
    //   - it's a mutating method
    //   - the url is queueable (idempotent / safe to replay)
    //   - AND the device is actually offline OR this is a true network error
    //     (NOT a timeout — timeouts mean the server may have completed).
    if (queueable && (isOffline || isRealNetworkError)) {
      const headers = { ...(cfg.headers || {}) };
      let body = cfg.data;
      if (typeof body === "string") {
        try { body = JSON.parse(body); } catch (_) { /* leave as-is */ }
      }
      try {
        await queueRequest({
          url,
          method: method.toUpperCase(),
          headers,
          body,
          kind: "api",
          label: `${method.toUpperCase()} ${cfg.url}`,
        });
      } catch (_) {
        return Promise.reject(error);
      }
      return Promise.resolve({
        status: 202,
        data: { queued: true, offline: true },
        config: cfg,
        headers: {},
        statusText: "Queued offline",
      });
    }
    // Everything else (timeouts, 4xx, 5xx, non-queueable URLs) bubbles
    // up to the caller. Components that hit AI endpoints MUST handle
    // these errors explicitly with a toast.
    return Promise.reject(error);
  },
);

export default api;

export function formatErr(detail, fallback = "Something went wrong") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Helpers exported for the offline indicator UI (so it can show why an
// entry is stuck without re-importing the whole pattern list).
export { NON_QUEUEABLE_PATTERNS, isNonQueueable };
